import { DOCUMENT } from '@angular/common';
import { find, findIndex, sortBy } from 'lodash';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { inject, Inject, Injectable, Injector } from '@angular/core';
import { SignInWithApple } from '@capacitor-community/apple-sign-in';
import { PushNotifications, Token } from '@capacitor/push-notifications';
import { catchError, map, map as rxMap, switchMap, take } from 'rxjs/operators';

import {
  UserType,
  UserRole,
  LoginType,
  IpDtoType,
  OutboundGQL,
  OutboundType,
  AddFcmTokenGQL,
  LoginForTargetGQL,
  WidgetVisualsType,
  LinkUserAccountGQL,
  CorporateLoginType,
  UserWithTargetInput,
  UserEventsSourceEnum,
  SuccessResponseDtoType,
  CurrentUserCompleteGQL,
  GetWidgetUserForTargetGQL,
  LoginWithAppleForTargetGQL,
  GetWidgetUserForTargetType,
  ReputationWithoutTargetType,
  WalletWithReputationDtoType,
  LoginWithGoogleForTargetGQL,
  LoginWithFacebookForTargetGQL,
  GetWidgetGuestUserForTargetGQL,
  RegisterForTargetWithOnboardGQL,
  GetWidgetGuestUserForTargetType,
  CorporateLoginWithActionTypeType,
  ListenForUserLoggedInForTargetGQL,
  CorporateOutboundNotificationInput,
  CreateOutboundCorporateNotificationGQL,
} from '@sifca-monorepo/widget-generator';
import { AuthUtils } from '@diktup/frontend/helpers';
import {
  DeleteUserGQL,
  DeleteUserReasonEnum,
  ResetPasswordForTargetGQL,
  ValidateEmailForTargetGQL,
  UpdateCurrentUserPasswordGQL,
  SendForgotPasswordMailToTargetGQL,
} from '@sifca-monorepo/ecommerce-generator';
import { MobileReputationService } from './reputation.service';
import { DeviceIdService } from './device-id.service';
import { Capacitor } from '@capacitor/core';

declare var FB: any;

@Injectable({
  providedIn: 'root',
})
export class MobileAuthService {
  private injector = inject(Injector);
  private readonly ipApiUrl = 'https://pro.ip-api.com/json?key=zHVOJD0lRu2UoXb';
  private ipAddress: BehaviorSubject<IpDtoType> = new BehaviorSubject(null);
  private currentUser: BehaviorSubject<UserType> = new BehaviorSubject<UserType>(null);
  private aggregatorUser: BehaviorSubject<UserType> = new BehaviorSubject<UserType>(null);
  private isWelcomeUser: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  private authenticated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  private lastWidgetOutbound: BehaviorSubject<OutboundType> = new BehaviorSubject(null);
  private wallet: BehaviorSubject<WalletWithReputationDtoType> = new BehaviorSubject(null);
  private unseenNotificationsCount: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  accessToken: string;
  token: string;

  get currentUser$(): Observable<UserType> {
    return this.currentUser.asObservable();
  }
  set currentUser$(value: any) {
    this.currentUser.next(value);
  }

  get aggregatorUser$(): Observable<UserType> {
    return this.aggregatorUser.asObservable();
  }
  set aggregatorUser$(value: any) {
    this.aggregatorUser.next(value);
  }

  set isWelcomeUser$(value: any) {
    this.isWelcomeUser.next(value);
  }
  get isWelcomeUser$(): Observable<boolean> {
    return this.isWelcomeUser.asObservable();
  }

  set authenticated$(value: any) {
    this.authenticated.next(value);
  }
  get authenticated$(): Observable<boolean> {
    return this.authenticated.asObservable();
  }

  get wallet$(): Observable<WalletWithReputationDtoType> {
    return this.wallet.asObservable();
  }
  set wallet$(value: any) {
    this.wallet.next(value);
  }

  get ipAddress$(): Observable<IpDtoType> {
    return this.ipAddress.asObservable();
  }

  get lastWidgetOutbound$(): Observable<OutboundType> {
    return this.lastWidgetOutbound.asObservable();
  }

  private get reputationService(): MobileReputationService {
    return this.injector.get(MobileReputationService);
  }

  constructor(
    private http: HttpClient,
    private outboundGQL: OutboundGQL,
    private deleteUserGQL: DeleteUserGQL,
    private addFcmTokenGQL: AddFcmTokenGQL,
    private deviceIdService: DeviceIdService,
    private loginForTargetGQL: LoginForTargetGQL,
    private linkUserAccountGQL: LinkUserAccountGQL,
    private currentUserCompleteGQL: CurrentUserCompleteGQL,
    private resetPasswordForTargetGQL: ResetPasswordForTargetGQL,
    private validateEmailForTargetGQL: ValidateEmailForTargetGQL,
    private getWidgetUserForTargetGQL: GetWidgetUserForTargetGQL,
    private loginWithAppleForTargetGQL: LoginWithAppleForTargetGQL,
    private loginWithGoogleForTargetGQL: LoginWithGoogleForTargetGQL,
    private updateCurrentUserPasswordGQL: UpdateCurrentUserPasswordGQL,
    private loginWithFacebookForTargetGQL: LoginWithFacebookForTargetGQL,
    private getWidgetGuestUserForTargetGQL: GetWidgetGuestUserForTargetGQL,
    private registerForTargetWithOnboardGQL: RegisterForTargetWithOnboardGQL,
    private listenForUserLoggedInForTargetGQL: ListenForUserLoggedInForTargetGQL,
    private sendForgotPasswordMailToTargetGQL: SendForgotPasswordMailToTargetGQL,
    private createOutboundCorporateNotificationGQL: CreateOutboundCorporateNotificationGQL,
    @Inject(DOCUMENT) protected document: Document,
  ) {
    this.accessToken = localStorage.getItem('elvkwdigttoken');
    if (this.accessToken) {
      this.authenticated.next(true);
    } else {
      this.authenticated.next(false);
    }
  }

  private fetchClientIp(): Observable<IpDtoType | null> {
    return this.http.get<any>(this.ipApiUrl).pipe(
      rxMap((response) => {
        return response?.query;
      }),
      catchError(() => of(null)),
    );
  }

  createOutboundCorporateNotification(input: CorporateOutboundNotificationInput): Observable<WidgetVisualsType[]> {
    return this.createOutboundCorporateNotificationGQL.mutate({ input: { ...input, target: { pos: localStorage.getItem('posId') } } }).pipe(
      map(({ data }: any) => {
        if (data) {
          return data.createOutboundCorporateNotification;
        }
      }),
    );
  }

  getOutboundById(id: string): Observable<OutboundType> {
    return this.outboundGQL.fetch({ id }).pipe(
      rxMap(({ data }: any) => {
        this.lastWidgetOutbound.next(data.outbound);
        return data.outbound;
      }),
    );
  }

  getWidgetGuestUserForTarget(): Observable<GetWidgetGuestUserForTargetType> {
    return this.getWidgetGuestUserForTargetGQL.fetch({ target: { pos: localStorage.getItem('posId') } }).pipe(
      rxMap(({ data }: any) => {
        this.lastWidgetOutbound.next(data.getWidgetGuestUserForTarget.outbound);
        return data.getWidgetGuestUserForTarget;
      }),
      switchMap((guestData) =>
        this.fetchClientIp().pipe(
          rxMap((clientIp) => {
            this.ipAddress.next(clientIp);
            return {
              ...guestData,
              clientIp,
            };
          }),
          catchError(() => of(guestData)),
        ),
      ),
    );
  }

  getWidgetUserForTarget(): Observable<GetWidgetUserForTargetType> {
    return this.getWidgetUserForTargetGQL
      .fetch({
        target: { pos: localStorage.getItem('posId') },
        ...(localStorage.getItem('userToken') ? { userToken: localStorage.getItem('userToken') } : {}),
      })
      .pipe(
        rxMap(({ data }: any) => {
          this.reputationService.wallet$ = data.getWidgetUserForTarget.wallet;
          const isOutboundPreview = this.document?.location?.pathname.includes('/outbound-preview');
          if (!isOutboundPreview) {
            this.lastWidgetOutbound.next(data.getWidgetUserForTarget.outbound);
          }
          this.aggregatorUser.next(data.getWidgetUserForTarget.aggregatorUser);
          this.unseenNotificationsCount.next(data.getWidgetUserForTarget.notificationsCount);
          this.currentUser.next(data?.getWidgetUserForTarget.currentUser);
          localStorage.setItem('elevkusr', this.currentUser.value?.id);
          return data.getWidgetUserForTarget;
        }),
        switchMap((userData) =>
          this.fetchClientIp().pipe(
            rxMap((clientIp) => {
              this.ipAddress.next(clientIp);
              return {
                ...userData,
                clientIp,
              };
            }),
            catchError(() => of(userData)),
          ),
        ),
      );
  }

  currentUserComplete(): Observable<GetWidgetUserForTargetType> {
    return this.currentUserCompleteGQL
      .fetch(
        {},
        ...(localStorage.getItem('aggregatorToken')
          ? [
              {
                context: {
                  headers: {
                    Authorization: `Bearer ${localStorage.getItem('aggregatorToken')}`,
                  },
                },
              },
            ]
          : []),
      )
      .pipe(
        rxMap(({ data }: any) => {
          this.currentUser.next(data?.currentUserComplete);
          return data.currentUserComplete;
        }),
      );
  }

  listenForUserLoggedInForTarget(reference: string, posId?: string): Observable<CorporateLoginWithActionTypeType> {
    return this.listenForUserLoggedInForTargetGQL.subscribe({ reference, target: { pos: posId || localStorage.getItem('posId') } }).pipe(
      rxMap(({ data }: any) => {
        return data.listenForUserLoggedInForTarget;
      }),
    );
  }

  updateCurrentUserPassword(oldPassword: string, newPassword: string): Observable<UserType> {
    return this.updateCurrentUserPasswordGQL.mutate({ oldPassword, newPassword }).pipe(
      rxMap(({ data }: any) => {
        return data.updateCurrentUserPassword;
      }),
    );
  }

  async addFcmToken() {
    const uuid = await this.deviceIdService.getDeviceUuid();
    const fcmToken = localStorage.getItem('fcmToken');
    this.addFcmTokenGQL
      .mutate({ fcmToken, uuid })
      .pipe(
        rxMap(({ data }: any) => {
          return data.addFcmToken;
        }),
      )
      .subscribe();
  }

  async registerPush() {
    let permStatus = await PushNotifications.checkPermissions();

    if (permStatus.receive !== 'granted') {
      permStatus = await PushNotifications.requestPermissions();
    }

    if (permStatus.receive === 'granted') {
      await PushNotifications.register();
    }

    PushNotifications.addListener('registration', async (token: Token) => {
      this.token = token.value;
      console.log('FCM Token received:', this.token);
      localStorage.setItem('fcmToken', this.token);
    });

    PushNotifications.addListener('registrationError', (err) => {
      console.error('Registration error:', err.error);
    });

    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('Push received:', notification);
    });
  }

  linkUserAccountWithReference(reference: string, token: string): Observable<any> {
    return this.linkUserAccountGQL
      .mutate(
        {
          input: { reference, target: { pos: localStorage.getItem('posId') } },
        },
        {
          context: {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        },
      )
      .pipe(
        map(({ data }: any) => {
          if (data) {
            return data.linkUserAccount;
          }
        }),
      );
  }

  linkUserAccountForAggregator(posId?: string): Observable<any> {
    const timestampInSeconds = Math.floor(Date.now() / 1000).toString();
    return this.linkUserAccountGQL
      .mutate({
        input: { reference: timestampInSeconds, target: { pos: posId } },
      })
      .pipe(
        map(({ data }: any) => {
          if (data) {
            return data.linkUserAccount;
          }
        }),
      );
  }

  resetPasswordForTarget(pos: string, password: string, token: string): Observable<any> {
    const isWeb = Capacitor.getPlatform() === 'web';
    return this.resetPasswordForTargetGQL.mutate({
      password,
      token,
      target: { pos },
      source: isWeb ? UserEventsSourceEnum.WEB : UserEventsSourceEnum.MOBILE,
    });
  }

  validateEmailForTarget(token: string): Observable<any> {
    return this.validateEmailForTargetGQL.mutate({ token }).pipe(
      map(({ data }: any) => {
        return data.validateEmailForTarget;
      }),
    );
  }

  forgotPassword(email: string, subject: string): Observable<any> {
    return this.sendForgotPasswordMailToTargetGQL.mutate({
      email,
      target: { pos: localStorage.getItem('posId') },
      subject,
    });
  }

  deleteUser(deleteData: { password: string; reason: string; description?: string }): Observable<any> {
    const userId = localStorage.getItem('userId') || this.currentUser.value?.id;

    if (!userId) {
      return throwError(() => new Error('User ID not found'));
    }

    return this.deleteUserGQL
      .mutate({
        password: deleteData.password,
        id: userId,
        reason: deleteData.reason as DeleteUserReasonEnum,
        description: deleteData.description,
      })
      .pipe(
        take(1),
        rxMap((result: any) => result),
      );
  }

  registerForTargetWithOnboard(input: UserWithTargetInput, aggregatorPosId?: string): Observable<CorporateLoginType> {
    return this.registerForTargetWithOnboardGQL
      .mutate({
        input: { ...input, target: { pos: aggregatorPosId ? aggregatorPosId : localStorage.getItem('posId') }, roles: [UserRole.CONSUMER] },
        source: UserEventsSourceEnum.WIDGET_WEB,
      })
      .pipe(
        rxMap(({ data }: any) => {
          if (data) {
            this.currentUser.next(data.registerForTargetWithOnboard.user);
            return data.registerForTargetWithOnboard;
          }
        }),
      );
  }

  linkUserAccount(reference: string): Observable<SuccessResponseDtoType> {
    return this.linkUserAccountGQL
      .mutate({
        input: {
          reference: reference,
          target: { pos: localStorage.getItem('posId') },
          source: UserEventsSourceEnum.WIDGET_WEB,
        },
      })
      .pipe(
        map(({ data }: any) => {
          if (data) {
            return data.linkUserAccount;
          }
        }),
      );
  }

  loginForTarget(input: any, aggregatorPosId?: string): Observable<LoginType> {
    return this.loginForTargetGQL
      .fetch({
        ...(input?.login ? { login: input.login } : {}),
        ...(input?.phone ? { phone: input.phone } : {}),
        password: input.password,
        target: { pos: aggregatorPosId ? aggregatorPosId : localStorage.getItem('posId') },
        source: UserEventsSourceEnum.WIDGET_WEB,
      })
      .pipe(
        rxMap(({ data, errors }: any) => {
          if (data) {
            this.currentUser.next(data.loginForTarget.user);
            return data.loginForTarget;
          }
          if (errors) {
            const exception = errors[0].extensions?.exception?.response?.error;
            throw exception;
          }
        }),
      );
  }

  check(): Observable<boolean> {
    if (this.authenticated.value === true) {
      return of(true);
    }
    // Check the access token availability
    if (!this.authenticated.value) {
      return of(false);
    }
    if (AuthUtils.isTokenExpired(this.accessToken)) {
      return of(false);
    }
    return of(false);
  }

  getUserLevelDetails(wallet: WalletWithReputationDtoType): {
    nextLevel: ReputationWithoutTargetType;
    isFinalLevel: boolean;
    currentLevel: ReputationWithoutTargetType;
    currentLevelPercentage: number;
  } {
    let nextLevel: ReputationWithoutTargetType;
    let isFinalLevel: boolean;
    let currentLevel: ReputationWithoutTargetType;
    let currentLevelPercentage: number;
    if (!wallet) return null;
    const reputationLevels = sortBy(wallet?.reputationLevels, 'rank');
    currentLevel = find(reputationLevels, (level, index: number) => {
      const last = index === reputationLevels.length - 1;
      return (
        (+wallet?.amount >= +level?.levelInterval?.max && +wallet?.amount < +level?.nextLevelMax && !last) ||
        (+wallet?.amount >= +reputationLevels[reputationLevels.length - 1]?.levelInterval?.max && last)
      );
    });
    const index = findIndex(reputationLevels, (rep) => rep?.reputationLevel === currentLevel?.reputationLevel);
    if (index <= reputationLevels?.length - 1 && index > -1) {
      nextLevel = reputationLevels[index + 1];
      isFinalLevel = false;
    } else {
      nextLevel = reputationLevels?.[0];
      isFinalLevel = false;
    }
    if (index === reputationLevels?.length - 1) {
      isFinalLevel = index === reputationLevels?.length - 1;
    }
    nextLevel?.levelInterval?.max - +wallet.amount;
    if (isFinalLevel) {
      currentLevelPercentage = 100;
    } else {
      currentLevelPercentage =
        ((+wallet.amount - nextLevel?.levelInterval?.min) / (nextLevel?.levelInterval?.max - nextLevel?.levelInterval?.min)) * 100;
    }
    return {
      nextLevel,
      isFinalLevel,
      currentLevel,
      currentLevelPercentage,
    };
  }

  async appleLogin(): Promise<any> {
    const result = await SignInWithApple.authorize({
      clientId: 'com.loyalcraft.signin',
      scopes: 'email name',
      redirectURI: 'loyalcraft.com',
    });

    return result.response;
  }

  loginWithAppleForTarget(authorizationData: any): Observable<LoginType> {
    return this.loginWithAppleForTargetGQL
      .fetch({
        authorizationData,
        target: { pos: localStorage.getItem('posId') },
      })
      .pipe(
        map(({ data }: any) => {
          this.currentUser.next(data.loginWithAppleForTarget.user);
          return data.loginWithAppleForTarget;
        }),
      );
  }

  facebookLogin(): Promise<any> {
    return new Promise((resolve, reject) => {
      (window as any).FB.login(
        (response: any) => {
          if (response.authResponse) {
            resolve(response.authResponse);
          } else {
            reject('User cancelled login or did not fully authorize.');
          }
        },
        { scope: 'email,public_profile' },
      );
    });
  }

  logoutWithFacebook() {
    FB.logout((response: any) => {
      console.log('User logged out:', response);
    });
  }

  loginWithFacebookForTarget(token: string, aggregatorPosId?: string): Observable<LoginType> {
    return this.loginWithFacebookForTargetGQL
      .fetch({ token, target: { pos: aggregatorPosId ? aggregatorPosId : localStorage.getItem('posId') } })
      .pipe(
        map(({ data }: any) => {
          this.currentUser.next(data.loginWithFacebookForTarget.user);
          return data.loginWithFacebookForTarget;
        }),
      );
  }

  loginWithGoogleForTarget(token: string, aggregatorPosId?: string): Observable<LoginType> {
    return this.loginWithGoogleForTargetGQL.fetch({ token, target: { pos: aggregatorPosId ? aggregatorPosId : localStorage.getItem('posId') } }).pipe(
      map(({ data }: any) => {
        this.currentUser.next(data.loginWithGoogleForTarget.user);
        return data.loginWithGoogleForTarget;
      }),
    );
  }
}
